import React, { useState, useEffect, useRef } from 'react'
import clsx from 'clsx'
import useVerifyOtp from 'sdk/hooks/useVerifyOtp';
import Button from 'components/shared/Button';
import { ReactComponent as IconResend } from 'images/resend.svg';
import useDigitInput from 'react-digit-input';
import styles from './ConfirmView.module.scss';

type ConfirmViewProps = {
  handleNext?: () => void,
  resendToken: () => void,
  phone: string,
  white?: boolean,
  small?: boolean,
  type?: string,
}

let timer: NodeJS.Timeout | undefined
const TIMEOUT = 60

function ConfirmView({
  handleNext = () => { },
  resendToken,
  phone,
  white = false,
  small = false,
  type = 'sms',
}: ConfirmViewProps) {
  const [token, setToken] = useState('')
  const [time, setTime] = useState(TIMEOUT)
  const [isError, setIsError] = useState(false)
  const firstInputRef = useRef(null)

  const verifyMutation = useVerifyOtp({ phone, token, type })

  const digits = useDigitInput({
    acceptedCharacters: /^\d$/,
    length: 6,
    value: token,
    onChange: setToken,
  });

  const startTimer = () => {
    if (timer) clearInterval(timer)
    setTime(TIMEOUT)
    timer = setInterval(() => {
      setTime((v) => (v > 1 ? v - 1 : 0))
    }, 1000)
  }

  const onResendCode = () => {
    setToken('')
    resendToken()
    startTimer()
  }

  useEffect(() => {
    if (timer && !time) {
      clearInterval(timer)
    }
  }, [time, timer])

  useEffect(() => {
    startTimer()
    return () => {
      if (timer) clearInterval(timer)
    }
  }, [])

  useEffect(() => {
    if (token.trim().length === 1) {
      setIsError(false)
    }
  }, [token])

  useEffect(() => {
    if (verifyMutation.isSuccess) {
      handleNext()
    }
  }, [verifyMutation.isSuccess])

  useEffect(() => {
    if (verifyMutation.isError) {
      setIsError(true)
      setToken('')
      if (firstInputRef?.current) {
        const firstInput = firstInputRef?.current as HTMLInputElement;
        firstInput.focus()
      }
    }
  }, [verifyMutation.isError])

  const cleanCode = token.replace(/\D/g, '')

  return (
    <>
      <div
        className={clsx(
          styles.inputGroup,
          isError && styles.inputGroupWithError,
          white && styles.inputGroupWhite,
          small && styles.inputGroupSmall,
        )}
      >
        <input inputMode="decimal" autoFocus {...digits[0]} ref={firstInputRef} />
        <input inputMode="decimal" {...digits[1]} />
        <input inputMode="decimal" {...digits[2]} />
        <input inputMode="decimal" {...digits[3]} />
        <input inputMode="decimal" {...digits[4]} />
        <input inputMode="decimal" {...digits[5]} />
      </div>

      {isError ? (
        <p className={styles.confirmError}>
          Invalid code. Please try again.
        </p>
      ) : null}

      <div
        className={clsx(
          styles.confirmInfo,
          white && styles.confirmInfoWhite,
        )}
      >
        <span className={styles.confirmInfoWasSent}>
          Your code was sent to
          {' '}
          <em>{phone}</em>
        </span>
        {time ? (
          <span className={styles.resendText}>
            Resend Code in
            {' '}
            <span className={styles.confirmInfoTime}>{time}s</span>
          </span>
        ) : ''}
      </div>
      <div className={styles.confirmActionButtons}>
        <div className={styles.confirmActionResend}>
          {!time && (
            <Button
              className={styles.resendButton}
              black={white}
              wide
              onClick={onResendCode}
              yellow
            >
              <IconResend />
              Resend Code
            </Button>
          )}
        </div>
        <div className={styles.confirmActionNext}>
          <Button
            disabled={cleanCode.length < 6}
            className={styles.btn}
            onClick={() => verifyMutation.mutate()}
            black={white}
            purple
          >
            Next
          </Button>
        </div>
      </div>
    </>
  )
}

export default ConfirmView
